import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MapWidget from "../../../widgets/map/mapWidget/MapWidget";
import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import { MapOverlayWidget } from "../../../widgets/map/mapAddons/mapOverlayWidget";

import { getAllObjectsLayer, getBasicObjectTypeLayer } from "../../../widgets/map/mapFeatures/layers";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { getObjectsOnWMSLayer } from "../../../widgets/map/mapFeatures/mapFunctions/getObjectsOnMapPoint/getObjectsOnMapPoint";

export const objectTypesDefaultFilters: Record<string, string> = {
    GeoMarkers: "GeoMarkerType in (2, 3)",
};

type ObjectTypeMeta = {
    name: string;
    mapLayer?: string;
    caption?: string;
};

export function MapObjectType() {
    const { objectTypeName } = useParams<{ objectTypeName: string }>();
    const navigate = useNavigate();

    const currentConfig = getItemWebStorage("currentConfig") ?? {};
    const { wmsServer, wmsVersion, workspace } = currentConfig as any;

    // предполагаем, что справочник типов почти статичен на время жизни страницы
    const objectTypes = useMemo<ObjectTypeMeta[]>(() => {
        const stored = getItemWebStorage("objectTypes");
        return Array.isArray(stored) ? (stored as ObjectTypeMeta[]) : [];
    }, []);

    const { title, layer, isInvalid } = useMemo(() => {
        if (!objectTypeName) return { title: "", layer: null as any, isInvalid: false };

        if (objectTypeName === "AllObjects") {
            return {
                title: "Карта «Все объекты»",
                layer: getAllObjectsLayer(),
                isInvalid: false,
            };
        }

        const meta = objectTypes.find((t) => t?.name === objectTypeName);
        const mapLayer = meta?.mapLayer;
        const caption = meta?.caption;

        if (!mapLayer || !wmsServer || !wmsVersion || !workspace) {
            return { title: "", layer: null as any, isInvalid: true };
        }

        const mainLayers = mapLayer.replace("MA", "MC");
        const layerFilter = objectTypesDefaultFilters[objectTypeName] ?? null;

        return {
            title: `Карта «${caption ?? objectTypeName}»`,
            layer: getBasicObjectTypeLayer(mainLayers, wmsServer, wmsVersion, workspace, layerFilter),
            isInvalid: false,
        };
    }, [objectTypeName, objectTypes, wmsServer, wmsVersion, workspace]);

    useEffect(() => {
        if (isInvalid) navigate("/app/error");
    }, [isInvalid, navigate]);

    const additionalLayers = useMemo(() => (layer ? [layer] : []), [layer]);

    const applyMapObjectsFind = useCallback(
        (map: any) => {
            if (!objectTypeName || !layer) return { off: () => { } };

            const clickFunc = (event: any) => getObjectsOnWMSLayer(event, objectTypeName, layer);
            map?.on("singleclick", clickFunc);

            return {
                off: () => map?.un("singleclick", clickFunc),
            };
        },
        [objectTypeName, layer]
    );

    const mapEventHandlers = useMemo(
        () => [
            {
                name: "objectsFind",
                func: applyMapObjectsFind,
            },
        ],
        [applyMapObjectsFind]
    );

    return (
        <>
            <ContentHeader title={title} objectTypeName={objectTypeName} type="map" />
            <div className="map-widget-with-toast">
                <MapWidget additionalLayers={additionalLayers} mapEventHandlers={mapEventHandlers} />
                <MapOverlayWidget />
            </div>
        </>
    );
}